import React, { useState, useEffect, useCallback } from 'react';
import {
  getCRMContacts, createCRMContact, updateCRMContact, deleteCRMContact,
  getContactActivities, createCRMActivity, deleteCRMActivity,
  getCRMDeals, createCRMDeal, updateCRMDeal, deleteCRMDeal,
  convertDealToProject, getClients, getUserNames,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plus, Search, Phone, Mail, Globe, Building2, User, Trash2, Edit,
  X, ChevronRight, Calendar, MessageSquare, StickyNote, FolderKanban,
  TrendingUp, ExternalLink, Clock, CheckCircle2, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

// ── constants ──────────────────────────────────────────────────────────────
const CONTACT_STATUSES = {
  cold:        { label: 'Studený',  color: 'bg-gray-100 text-gray-700 border-gray-300' },
  contacted:   { label: 'Osloven',  color: 'bg-blue-100 text-blue-700 border-blue-300' },
  interested:  { label: 'Zájem',    color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  meeting:     { label: 'Schůzka',  color: 'bg-purple-100 text-purple-700 border-purple-300' },
  client:      { label: 'Klient',   color: 'bg-green-100 text-green-700 border-green-300' },
  lost:        { label: 'Nevhodný', color: 'bg-red-100 text-red-600 border-red-300' },
};

const SOURCES = {
  linkedin:   'LinkedIn',
  referral:   'Doporučení',
  web:        'Web',
  event:      'Event',
  outbound:   'Outbound',
  other:      'Jiné',
};

const ACTIVITY_TYPES = {
  call:    { label: 'Hovor',    icon: Phone },
  email:   { label: 'Email',    icon: Mail },
  meeting: { label: 'Schůzka',  icon: Calendar },
  message: { label: 'Zpráva',   icon: MessageSquare },
  note:    { label: 'Poznámka', icon: StickyNote },
};

const DEAL_STAGES = {
  identified:  { label: 'Identifikováno', color: 'bg-gray-100 text-gray-700 border-gray-300' },
  contacted:   { label: 'Kontaktováno',   color: 'bg-blue-100 text-blue-700 border-blue-300' },
  meeting:     { label: 'Schůzka',        color: 'bg-purple-100 text-purple-700 border-purple-300' },
  proposal:    { label: 'Nabídka',        color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  negotiation: { label: 'Jednání',        color: 'bg-orange-100 text-orange-700 border-orange-300' },
  won:         { label: 'Vyhráno',        color: 'bg-green-100 text-green-700 border-green-300' },
  lost:        { label: 'Prohráno',       color: 'bg-red-100 text-red-600 border-red-300' },
};

const EMPTY_CONTACT = {
  name: '', email: '', phone: '', position: '', company_name: '',
  client_id: '', linkedin: '', source: '', status: 'cold', notes: '', assigned_to: '',
};

const EMPTY_DEAL = {
  name: '', contact_id: '', client_id: '', value: '',
  probability: '', stage: 'identified', expected_close_date: '', notes: '', assigned_to: '',
};

const today = new Date().toISOString().slice(0, 10);

// ── StatusBadge ────────────────────────────────────────────────────────────
const StatusBadge = ({ status, map }) => {
  const s = map[status] || { label: status, color: 'bg-gray-100 text-gray-600 border-gray-200' };
  return (
    <span className={`px-2 py-0.5 text-xs font-oswald uppercase border ${s.color}`}>
      {s.label}
    </span>
  );
};

// ── ContactForm ────────────────────────────────────────────────────────────
const ContactForm = ({ data, onChange, clients, users }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <div className="space-y-1 sm:col-span-2">
      <Label className="font-oswald uppercase text-xs tracking-wide">Jméno *</Label>
      <Input value={data.name} onChange={e => onChange('name', e.target.value)}
        className="border-2 border-gray-200 focus:border-black rounded-none" />
    </div>
    <div className="space-y-1">
      <Label className="font-oswald uppercase text-xs tracking-wide">Email</Label>
      <Input type="email" value={data.email} onChange={e => onChange('email', e.target.value)}
        className="border-2 border-gray-200 focus:border-black rounded-none" />
    </div>
    <div className="space-y-1">
      <Label className="font-oswald uppercase text-xs tracking-wide">Telefon</Label>
      <Input value={data.phone} onChange={e => onChange('phone', e.target.value)}
        className="border-2 border-gray-200 focus:border-black rounded-none" />
    </div>
    <div className="space-y-1">
      <Label className="font-oswald uppercase text-xs tracking-wide">Pozice</Label>
      <Input value={data.position} onChange={e => onChange('position', e.target.value)}
        className="border-2 border-gray-200 focus:border-black rounded-none" />
    </div>
    <div className="space-y-1">
      <Label className="font-oswald uppercase text-xs tracking-wide">Firma (text)</Label>
      <Input value={data.company_name} onChange={e => onChange('company_name', e.target.value)}
        placeholder="Volný text nebo vyberte klienta níže"
        className="border-2 border-gray-200 focus:border-black rounded-none" />
    </div>
    <div className="space-y-1">
      <Label className="font-oswald uppercase text-xs tracking-wide">Napojit na klienta</Label>
      <Select value={data.client_id || 'none'} onValueChange={v => onChange('client_id', v === 'none' ? '' : v)}>
        <SelectTrigger className="border-2 border-gray-200 rounded-none">
          <SelectValue placeholder="— žádný —" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">— žádný —</SelectItem>
          {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-1">
      <Label className="font-oswald uppercase text-xs tracking-wide">LinkedIn URL</Label>
      <Input value={data.linkedin} onChange={e => onChange('linkedin', e.target.value)}
        placeholder="https://linkedin.com/in/..."
        className="border-2 border-gray-200 focus:border-black rounded-none" />
    </div>
    <div className="space-y-1">
      <Label className="font-oswald uppercase text-xs tracking-wide">Zdroj</Label>
      <Select value={data.source || 'none'} onValueChange={v => onChange('source', v === 'none' ? '' : v)}>
        <SelectTrigger className="border-2 border-gray-200 rounded-none">
          <SelectValue placeholder="— vyberte —" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">— vyberte —</SelectItem>
          {Object.entries(SOURCES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-1">
      <Label className="font-oswald uppercase text-xs tracking-wide">Stav</Label>
      <Select value={data.status} onValueChange={v => onChange('status', v)}>
        <SelectTrigger className="border-2 border-gray-200 rounded-none">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(CONTACT_STATUSES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-1">
      <Label className="font-oswald uppercase text-xs tracking-wide">Zodpovědný</Label>
      <Select value={data.assigned_to || 'none'} onValueChange={v => onChange('assigned_to', v === 'none' ? '' : v)}>
        <SelectTrigger className="border-2 border-gray-200 rounded-none">
          <SelectValue placeholder="— nikdo —" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">— nikdo —</SelectItem>
          {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-1 sm:col-span-2">
      <Label className="font-oswald uppercase text-xs tracking-wide">Poznámka</Label>
      <Textarea value={data.notes} onChange={e => onChange('notes', e.target.value)} rows={3}
        className="border-2 border-gray-200 focus:border-black rounded-none resize-none" />
    </div>
  </div>
);

// ── DealForm ───────────────────────────────────────────────────────────────
const DealForm = ({ data, onChange, contacts, clients, users }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <div className="space-y-1 sm:col-span-2">
      <Label className="font-oswald uppercase text-xs tracking-wide">Název příležitosti *</Label>
      <Input value={data.name} onChange={e => onChange('name', e.target.value)}
        className="border-2 border-gray-200 focus:border-black rounded-none" />
    </div>
    <div className="space-y-1">
      <Label className="font-oswald uppercase text-xs tracking-wide">Kontakt</Label>
      <Select value={data.contact_id || 'none'} onValueChange={v => onChange('contact_id', v === 'none' ? '' : v)}>
        <SelectTrigger className="border-2 border-gray-200 rounded-none">
          <SelectValue placeholder="— žádný —" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">— žádný —</SelectItem>
          {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}{c.company_name ? ` (${c.company_name})` : ''}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-1">
      <Label className="font-oswald uppercase text-xs tracking-wide">Klient (firma)</Label>
      <Select value={data.client_id || 'none'} onValueChange={v => onChange('client_id', v === 'none' ? '' : v)}>
        <SelectTrigger className="border-2 border-gray-200 rounded-none">
          <SelectValue placeholder="— žádný —" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">— žádný —</SelectItem>
          {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-1">
      <Label className="font-oswald uppercase text-xs tracking-wide">Hodnota (Kč)</Label>
      <Input type="number" min="0" value={data.value} onChange={e => onChange('value', e.target.value)}
        className="border-2 border-gray-200 focus:border-black rounded-none" />
    </div>
    <div className="space-y-1">
      <Label className="font-oswald uppercase text-xs tracking-wide">Pravděpodobnost (%)</Label>
      <Input type="number" min="0" max="100" value={data.probability} onChange={e => onChange('probability', e.target.value)}
        className="border-2 border-gray-200 focus:border-black rounded-none" />
    </div>
    <div className="space-y-1">
      <Label className="font-oswald uppercase text-xs tracking-wide">Fáze</Label>
      <Select value={data.stage} onValueChange={v => onChange('stage', v)}>
        <SelectTrigger className="border-2 border-gray-200 rounded-none"><SelectValue /></SelectTrigger>
        <SelectContent>
          {Object.entries(DEAL_STAGES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-1">
      <Label className="font-oswald uppercase text-xs tracking-wide">Očekávané uzavření</Label>
      <Input type="date" value={data.expected_close_date} onChange={e => onChange('expected_close_date', e.target.value)}
        className="border-2 border-gray-200 focus:border-black rounded-none" />
    </div>
    <div className="space-y-1">
      <Label className="font-oswald uppercase text-xs tracking-wide">Zodpovědný</Label>
      <Select value={data.assigned_to || 'none'} onValueChange={v => onChange('assigned_to', v === 'none' ? '' : v)}>
        <SelectTrigger className="border-2 border-gray-200 rounded-none">
          <SelectValue placeholder="— nikdo —" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">— nikdo —</SelectItem>
          {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-1 sm:col-span-2">
      <Label className="font-oswald uppercase text-xs tracking-wide">Poznámka</Label>
      <Textarea value={data.notes} onChange={e => onChange('notes', e.target.value)} rows={3}
        className="border-2 border-gray-200 focus:border-black rounded-none resize-none" />
    </div>
  </div>
);

// ── ContactDetailDialog ────────────────────────────────────────────────────
const ContactDetailDialog = ({ contact, open, onClose, onUpdated, clients, users }) => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState(EMPTY_CONTACT);
  const [activities, setActivities] = useState([]);
  const [loadingAct, setLoadingAct] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newActivity, setNewActivity] = useState({
    type: 'call', date: today, description: '', result: '',
  });
  const [addingActivity, setAddingActivity] = useState(false);

  useEffect(() => {
    if (contact && open) {
      setFormData({
        name: contact.name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        position: contact.position || '',
        company_name: contact.company_name || '',
        client_id: contact.client_id || '',
        linkedin: contact.linkedin || '',
        source: contact.source || '',
        status: contact.status || 'cold',
        notes: contact.notes || '',
        assigned_to: contact.assigned_to || '',
      });
      setEditing(false);
      fetchActivities();
    }
  }, [contact, open]);

  const fetchActivities = async () => {
    if (!contact) return;
    setLoadingAct(true);
    try {
      const res = await getContactActivities(contact.id);
      setActivities(res.data);
    } catch {
      setActivities([]);
    } finally {
      setLoadingAct(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { toast.error('Jméno je povinné'); return; }
    setSaving(true);
    try {
      await updateCRMContact(contact.id, {
        ...formData,
        client_id: formData.client_id || null,
        assigned_to: formData.assigned_to || null,
        source: formData.source || null,
      });
      toast.success('Kontakt uložen');
      setEditing(false);
      onUpdated();
    } catch {
      toast.error('Chyba při ukládání');
    } finally {
      setSaving(false);
    }
  };

  const handleAddActivity = async () => {
    if (!newActivity.description.trim()) { toast.error('Popis aktivity je povinný'); return; }
    setAddingActivity(true);
    try {
      await createCRMActivity({ ...newActivity, contact_id: contact.id });
      setNewActivity({ type: 'call', date: today, description: '', result: '' });
      fetchActivities();
      onUpdated();
      toast.success('Aktivita přidána');
    } catch {
      toast.error('Chyba při přidávání aktivity');
    } finally {
      setAddingActivity(false);
    }
  };

  const handleDeleteActivity = async (id) => {
    try {
      await deleteCRMActivity(id);
      setActivities(prev => prev.filter(a => a.id !== id));
      onUpdated();
    } catch {
      toast.error('Chyba při mazání aktivity');
    }
  };

  if (!contact) return null;
  const ActIcon = ({ type }) => {
    const T = ACTIVITY_TYPES[type]?.icon || StickyNote;
    return <T size={14} />;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-2 border-black rounded-none shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] p-0">
        {/* Header */}
        <div className="p-6 border-b-2 border-black flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="font-oswald text-2xl font-bold uppercase">{contact.name}</h2>
              <StatusBadge status={contact.status} map={CONTACT_STATUSES} />
            </div>
            {(contact.position || contact.company_name || contact.client_name) && (
              <p className="font-serif text-gray-600 mt-1">
                {contact.position}{contact.position && (contact.company_name || contact.client_name) ? ' · ' : ''}
                {contact.client_name || contact.company_name}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {!editing && (
              <button onClick={() => setEditing(true)}
                className="p-2 border-2 border-black hover:bg-[#F4E600] transition-colors">
                <Edit size={16} />
              </button>
            )}
            <button onClick={onClose} className="p-2 border-2 border-black hover:bg-gray-100 transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Contact info / Edit form */}
          {editing ? (
            <div className="space-y-4">
              <ContactForm data={formData} onChange={(k, v) => setFormData(p => ({ ...p, [k]: v }))} clients={clients} users={users} />
              <div className="flex gap-3 pt-2">
                <Button onClick={handleSave} disabled={saving}
                  className="bg-[#F4E600] text-black font-oswald uppercase border-2 border-black rounded-none shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] transition-all">
                  {saving ? 'Ukládání...' : 'Uložit'}
                </Button>
                <Button variant="outline" onClick={() => setEditing(false)}
                  className="border-2 border-black rounded-none">Zrušit</Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-sm font-serif hover:text-[#006CB6]">
                  <Mail size={15} className="text-gray-400" /> {contact.email}
                </a>
              )}
              {contact.phone && (
                <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-sm font-serif hover:text-[#006CB6]">
                  <Phone size={15} className="text-gray-400" /> {contact.phone}
                </a>
              )}
              {contact.linkedin && (
                <a href={contact.linkedin} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm font-serif hover:text-[#006CB6] col-span-full">
                  <ExternalLink size={15} className="text-gray-400" /> LinkedIn profil
                </a>
              )}
              {contact.source && (
                <div className="flex items-center gap-2 text-sm font-serif text-gray-600">
                  <Globe size={15} className="text-gray-400" /> Zdroj: {SOURCES[contact.source] || contact.source}
                </div>
              )}
              {contact.assigned_to_name && (
                <div className="flex items-center gap-2 text-sm font-serif text-gray-600">
                  <User size={15} className="text-gray-400" /> {contact.assigned_to_name}
                </div>
              )}
              {contact.notes && (
                <div className="sm:col-span-2 p-3 bg-gray-50 border border-gray-200 text-sm font-serif text-gray-700 whitespace-pre-wrap">
                  {contact.notes}
                </div>
              )}
            </div>
          )}

          {/* Activities */}
          {!editing && (
            <div className="border-t-2 border-black pt-4">
              <h3 className="font-oswald text-lg font-bold uppercase mb-4">Aktivity ({activities.length})</h3>

              {/* Add activity */}
              <div className="border-2 border-black p-4 mb-4 space-y-3 bg-gray-50">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="font-oswald uppercase text-xs tracking-wide">Typ</Label>
                    <Select value={newActivity.type} onValueChange={v => setNewActivity(p => ({ ...p, type: v }))}>
                      <SelectTrigger className="border-2 border-gray-300 rounded-none bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(ACTIVITY_TYPES).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="font-oswald uppercase text-xs tracking-wide">Datum</Label>
                    <Input type="date" value={newActivity.date}
                      onChange={e => setNewActivity(p => ({ ...p, date: e.target.value }))}
                      className="border-2 border-gray-300 focus:border-black rounded-none bg-white" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Popis *</Label>
                  <Textarea value={newActivity.description} rows={2}
                    onChange={e => setNewActivity(p => ({ ...p, description: e.target.value }))}
                    placeholder="Co se dělo..."
                    className="border-2 border-gray-300 focus:border-black rounded-none resize-none bg-white" />
                </div>
                <div className="space-y-1">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Výsledek</Label>
                  <Input value={newActivity.result}
                    onChange={e => setNewActivity(p => ({ ...p, result: e.target.value }))}
                    placeholder="Volitelně — co z toho vzešlo"
                    className="border-2 border-gray-300 focus:border-black rounded-none bg-white" />
                </div>
                <Button onClick={handleAddActivity} disabled={addingActivity}
                  className="w-full bg-black text-white font-oswald uppercase border-2 border-black rounded-none hover:bg-gray-900 transition-all">
                  <Plus size={16} className="mr-2" /> {addingActivity ? 'Ukládám...' : 'Přidat aktivitu'}
                </Button>
              </div>

              {/* Activity list */}
              {loadingAct ? (
                <p className="text-sm text-gray-500 font-serif">Načítám...</p>
              ) : activities.length === 0 ? (
                <p className="text-sm text-gray-500 font-serif">Zatím žádné aktivity.</p>
              ) : (
                <div className="space-y-2">
                  {activities.map(a => {
                    const T = ACTIVITY_TYPES[a.type]?.icon || StickyNote;
                    const canDel = user?.is_admin || a.created_by === user?.id;
                    return (
                      <div key={a.id} className="flex gap-3 p-3 border border-gray-200 bg-white group">
                        <div className="mt-0.5 text-gray-400"><T size={15} /></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-oswald text-xs uppercase text-gray-500">
                              {ACTIVITY_TYPES[a.type]?.label}
                            </span>
                            <span className="text-xs text-gray-400 font-serif">{a.date}</span>
                            {a.created_by_name && (
                              <span className="text-xs text-gray-400 font-serif">· {a.created_by_name}</span>
                            )}
                          </div>
                          <p className="text-sm font-serif">{a.description}</p>
                          {a.result && (
                            <p className="text-xs text-gray-500 font-serif mt-1 italic">→ {a.result}</p>
                          )}
                        </div>
                        {canDel && (
                          <button onClick={() => handleDeleteActivity(a.id)}
                            className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-all">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Main CRM page ──────────────────────────────────────────────────────────
const CRM = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('contacts');

  // shared data
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);

  // contacts state
  const [contacts, setContacts] = useState([]);
  const [contactLoading, setContactLoading] = useState(true);
  const [contactSearch, setContactSearch] = useState('');
  const [contactStatusFilter, setContactStatusFilter] = useState('all');
  const [contactSourceFilter, setContactSourceFilter] = useState('all');
  const [detailContact, setDetailContact] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT);
  const [contactSaving, setContactSaving] = useState(false);

  // deals state
  const [deals, setDeals] = useState([]);
  const [dealLoading, setDealLoading] = useState(true);
  const [dealStageFilter, setDealStageFilter] = useState('all');
  const [dealDialogOpen, setDealDialogOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [dealForm, setDealForm] = useState(EMPTY_DEAL);
  const [dealSaving, setDealSaving] = useState(false);

  // load shared
  useEffect(() => {
    Promise.all([
      getClients().catch(() => ({ data: [] })),
      getUserNames().catch(() => ({ data: [] })),
    ]).then(([c, u]) => {
      setClients(c.data || []);
      setUsers(u.data || []);
    });
  }, []);

  // load contacts
  const fetchContacts = useCallback(async () => {
    setContactLoading(true);
    try {
      const params = {};
      if (contactSearch) params.search = contactSearch;
      if (contactStatusFilter !== 'all') params.status = contactStatusFilter;
      if (contactSourceFilter !== 'all') params.source = contactSourceFilter;
      const res = await getCRMContacts(params);
      setContacts(res.data);
    } catch {
      toast.error('Chyba při načítání kontaktů');
    } finally {
      setContactLoading(false);
    }
  }, [contactSearch, contactStatusFilter, contactSourceFilter]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  // load deals
  const fetchDeals = useCallback(async () => {
    setDealLoading(true);
    try {
      const params = {};
      if (dealStageFilter !== 'all') params.stage = dealStageFilter;
      const res = await getCRMDeals(params);
      setDeals(res.data);
    } catch {
      toast.error('Chyba při načítání příležitostí');
    } finally {
      setDealLoading(false);
    }
  }, [dealStageFilter]);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  // ── contact CRUD ─────────────────────────────────────────────────────────
  const openNewContact = () => {
    setEditingContact(null);
    setContactForm(EMPTY_CONTACT);
    setContactDialogOpen(true);
  };

  const openEditContact = (c, e) => {
    e.stopPropagation();
    setEditingContact(c);
    setContactForm({
      name: c.name || '', email: c.email || '', phone: c.phone || '',
      position: c.position || '', company_name: c.company_name || '',
      client_id: c.client_id || '', linkedin: c.linkedin || '',
      source: c.source || '', status: c.status || 'cold',
      notes: c.notes || '', assigned_to: c.assigned_to || '',
    });
    setContactDialogOpen(true);
  };

  const handleContactSave = async () => {
    if (!contactForm.name.trim()) { toast.error('Jméno je povinné'); return; }
    setContactSaving(true);
    try {
      const payload = {
        ...contactForm,
        client_id: contactForm.client_id || null,
        assigned_to: contactForm.assigned_to || null,
        source: contactForm.source || null,
      };
      if (editingContact) {
        await updateCRMContact(editingContact.id, payload);
        toast.success('Kontakt upraven');
      } else {
        await createCRMContact(payload);
        toast.success('Kontakt vytvořen');
      }
      setContactDialogOpen(false);
      fetchContacts();
    } catch {
      toast.error('Chyba při ukládání');
    } finally {
      setContactSaving(false);
    }
  };

  const handleDeleteContact = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Smazat kontakt i se všemi aktivitami?')) return;
    try {
      await deleteCRMContact(id);
      toast.success('Kontakt smazán');
      fetchContacts();
      if (detailContact?.id === id) setDetailOpen(false);
    } catch {
      toast.error('Chyba při mazání');
    }
  };

  const openDetail = (c) => {
    setDetailContact(c);
    setDetailOpen(true);
  };

  // ── deal CRUD ─────────────────────────────────────────────────────────────
  const openNewDeal = () => {
    setEditingDeal(null);
    setDealForm(EMPTY_DEAL);
    setDealDialogOpen(true);
  };

  const openEditDeal = (d) => {
    setEditingDeal(d);
    setDealForm({
      name: d.name || '', contact_id: d.contact_id || '',
      client_id: d.client_id || '', value: d.value ?? '',
      probability: d.probability ?? '', stage: d.stage || 'identified',
      expected_close_date: d.expected_close_date || '',
      notes: d.notes || '', assigned_to: d.assigned_to || '',
    });
    setDealDialogOpen(true);
  };

  const handleDealSave = async () => {
    if (!dealForm.name.trim()) { toast.error('Název je povinný'); return; }
    setDealSaving(true);
    try {
      const payload = {
        ...dealForm,
        contact_id: dealForm.contact_id || null,
        client_id: dealForm.client_id || null,
        assigned_to: dealForm.assigned_to || null,
        value: dealForm.value !== '' ? parseFloat(dealForm.value) : null,
        probability: dealForm.probability !== '' ? parseInt(dealForm.probability) : null,
        expected_close_date: dealForm.expected_close_date || null,
      };
      if (editingDeal) {
        await updateCRMDeal(editingDeal.id, payload);
        toast.success('Příležitost upravena');
      } else {
        await createCRMDeal(payload);
        toast.success('Příležitost vytvořena');
      }
      setDealDialogOpen(false);
      fetchDeals();
    } catch {
      toast.error('Chyba při ukládání');
    } finally {
      setDealSaving(false);
    }
  };

  const handleDeleteDeal = async (id) => {
    if (!window.confirm('Smazat příležitost?')) return;
    try {
      await deleteCRMDeal(id);
      toast.success('Příležitost smazána');
      fetchDeals();
    } catch {
      toast.error('Chyba při mazání');
    }
  };

  const handleConvert = async (id) => {
    if (!window.confirm('Převést příležitost na projekt? Příležitost bude označena jako Vyhráno.')) return;
    try {
      const res = await convertDealToProject(id);
      toast.success('Projekt vytvořen!');
      fetchDeals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Chyba při převodu');
    }
  };

  const formatCZK = (v) => v != null ? new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }).format(v) : '—';

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-oswald text-4xl font-bold uppercase tracking-tight">CRM</h1>
          <p className="font-serif text-gray-600 mt-1">Obchodní kontakty a příležitosti</p>
        </div>
        <Button
          onClick={activeTab === 'contacts' ? openNewContact : openNewDeal}
          className="bg-[#F4E600] text-black font-oswald uppercase tracking-wide border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
        >
          <Plus size={18} className="mr-2" />
          {activeTab === 'contacts' ? 'Nový kontakt' : 'Nová příležitost'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex border-b-2 border-black">
        {[
          { key: 'contacts', label: `Kontakty (${contacts.length})`, icon: User },
          { key: 'deals', label: `Příležitosti (${deals.length})`, icon: TrendingUp },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-6 py-3 font-oswald uppercase tracking-wide text-sm border-r-2 border-black transition-colors ${
                activeTab === tab.key ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'
              }`}>
              <Icon size={16} /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── CONTACTS TAB ── */}
      {activeTab === 'contacts' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-48">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={contactSearch} onChange={e => setContactSearch(e.target.value)}
                placeholder="Hledat jméno, email, firma..."
                className="pl-9 border-2 border-gray-200 focus:border-black rounded-none" />
            </div>
            <Select value={contactStatusFilter} onValueChange={setContactStatusFilter}>
              <SelectTrigger className="w-44 border-2 border-gray-200 rounded-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všechny stavy</SelectItem>
                {Object.entries(CONTACT_STATUSES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={contactSourceFilter} onValueChange={setContactSourceFilter}>
              <SelectTrigger className="w-44 border-2 border-gray-200 rounded-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všechny zdroje</SelectItem>
                {Object.entries(SOURCES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Contact table */}
          {contactLoading ? (
            <div className="text-center py-12 text-gray-500 font-serif">Načítám...</div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-300">
              <User size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="font-oswald uppercase text-gray-500">Žádné kontakty</p>
              <p className="font-serif text-sm text-gray-400 mt-1">Přidejte první kontakt tlačítkem výše</p>
            </div>
          ) : (
            <div className="border-2 border-black">
              <div className="hidden sm:grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr_auto] bg-black text-white text-xs font-oswald uppercase tracking-wide px-4 py-2 gap-4">
                <span>Jméno / Firma</span>
                <span>Kontakt</span>
                <span>Stav</span>
                <span>Zdroj</span>
                <span>Aktivit</span>
                <span></span>
              </div>
              {contacts.map((c, i) => (
                <div key={c.id}
                  onClick={() => openDetail(c)}
                  className={`grid grid-cols-1 sm:grid-cols-[2fr_1.5fr_1fr_1fr_1fr_auto] gap-4 px-4 py-3 cursor-pointer hover:bg-[#F4E600]/20 transition-colors group ${i > 0 ? 'border-t border-gray-200' : ''}`}
                >
                  <div>
                    <p className="font-oswald font-bold text-sm uppercase">{c.name}</p>
                    {(c.client_name || c.company_name) && (
                      <p className="text-xs text-gray-500 font-serif flex items-center gap-1">
                        <Building2 size={11} /> {c.client_name || c.company_name}
                      </p>
                    )}
                    {c.position && <p className="text-xs text-gray-400 font-serif">{c.position}</p>}
                  </div>
                  <div className="flex flex-col gap-0.5 justify-center">
                    {c.email && <span className="text-xs font-serif text-gray-600 truncate">{c.email}</span>}
                    {c.phone && <span className="text-xs font-serif text-gray-600">{c.phone}</span>}
                  </div>
                  <div className="flex items-center">
                    <StatusBadge status={c.status} map={CONTACT_STATUSES} />
                  </div>
                  <div className="flex items-center">
                    {c.source ? (
                      <span className="text-xs font-serif text-gray-600">{SOURCES[c.source] || c.source}</span>
                    ) : <span className="text-gray-300">—</span>}
                  </div>
                  <div className="flex items-center gap-1 text-xs font-serif text-gray-500">
                    <Clock size={12} /> {c.activities_count}
                    {c.last_activity_date && <span className="text-gray-400 hidden lg:inline">· {c.last_activity_date}</span>}
                  </div>
                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    <button onClick={e => openEditContact(c, e)}
                      className="p-1.5 border border-gray-200 hover:border-black hover:bg-[#F4E600] transition-colors opacity-0 group-hover:opacity-100">
                      <Edit size={13} />
                    </button>
                    <button onClick={e => handleDeleteContact(c.id, e)}
                      className="p-1.5 border border-gray-200 hover:border-red-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100">
                      <Trash2 size={13} />
                    </button>
                    <ChevronRight size={16} className="text-gray-300 group-hover:text-black transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── DEALS TAB ── */}
      {activeTab === 'deals' && (
        <div className="space-y-4">
          {/* Pipeline summary */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(DEAL_STAGES).map(([k, v]) => {
              const count = deals.filter(d => d.stage === k).length;
              const total = deals.filter(d => d.stage === k).reduce((s, d) => s + (d.value || 0), 0);
              return (
                <button key={k} onClick={() => setDealStageFilter(dealStageFilter === k ? 'all' : k)}
                  className={`px-3 py-2 border-2 text-xs font-oswald uppercase transition-colors ${
                    dealStageFilter === k ? 'border-black bg-black text-white' : 'border-gray-200 hover:border-black'
                  }`}>
                  {v.label} ({count})
                  {total > 0 && <span className="ml-1 opacity-70">{formatCZK(total)}</span>}
                </button>
              );
            })}
            {dealStageFilter !== 'all' && (
              <button onClick={() => setDealStageFilter('all')}
                className="px-3 py-2 border-2 border-gray-200 text-xs font-oswald uppercase hover:border-black flex items-center gap-1">
                <X size={12} /> Vše
              </button>
            )}
          </div>

          {/* Deals table */}
          {dealLoading ? (
            <div className="text-center py-12 text-gray-500 font-serif">Načítám...</div>
          ) : deals.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-300">
              <TrendingUp size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="font-oswald uppercase text-gray-500">Žádné příležitosti</p>
            </div>
          ) : (
            <div className="border-2 border-black">
              <div className="hidden sm:grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr_auto] bg-black text-white text-xs font-oswald uppercase tracking-wide px-4 py-2 gap-4">
                <span>Příležitost</span>
                <span>Kontakt / Klient</span>
                <span>Fáze</span>
                <span>Hodnota</span>
                <span>Uzavření</span>
                <span></span>
              </div>
              {deals.map((d, i) => (
                <div key={d.id}
                  className={`grid grid-cols-1 sm:grid-cols-[2fr_1.5fr_1fr_1fr_1fr_auto] gap-4 px-4 py-3 group ${i > 0 ? 'border-t border-gray-200' : ''} hover:bg-gray-50 transition-colors`}
                >
                  <div>
                    <p className="font-oswald font-bold text-sm uppercase">{d.name}</p>
                    {d.assigned_to_name && <p className="text-xs text-gray-400 font-serif">{d.assigned_to_name}</p>}
                  </div>
                  <div className="flex flex-col gap-0.5 justify-center">
                    {d.contact_name && <span className="text-xs font-serif text-gray-700">{d.contact_name}</span>}
                    {d.client_name && <span className="text-xs font-serif text-gray-500 flex items-center gap-1"><Building2 size={11} />{d.client_name}</span>}
                  </div>
                  <div className="flex items-center">
                    <StatusBadge status={d.stage} map={DEAL_STAGES} />
                  </div>
                  <div className="flex items-center text-sm font-serif">
                    {d.value != null ? (
                      <span>{formatCZK(d.value)}{d.probability != null ? <span className="text-xs text-gray-400 ml-1">({d.probability}%)</span> : ''}</span>
                    ) : '—'}
                  </div>
                  <div className="flex items-center text-xs font-serif text-gray-500">
                    {d.expected_close_date || '—'}
                  </div>
                  <div className="flex items-center gap-1">
                    {d.stage !== 'won' && d.stage !== 'lost' && (
                      <button onClick={() => handleConvert(d.id)} title="Převést na projekt"
                        className="p-1.5 border border-gray-200 hover:border-green-500 hover:text-green-600 transition-colors opacity-0 group-hover:opacity-100">
                        <FolderKanban size={13} />
                      </button>
                    )}
                    <button onClick={() => openEditDeal(d)}
                      className="p-1.5 border border-gray-200 hover:border-black hover:bg-[#F4E600] transition-colors opacity-0 group-hover:opacity-100">
                      <Edit size={13} />
                    </button>
                    <button onClick={() => handleDeleteDeal(d.id)}
                      className="p-1.5 border border-gray-200 hover:border-red-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pipeline total */}
          {deals.length > 0 && (
            <div className="border-2 border-black p-4 bg-white flex flex-wrap gap-6">
              {[
                { label: 'Celková pipeline', value: deals.filter(d => !['won','lost'].includes(d.stage)).reduce((s,d) => s+(d.value||0), 0) },
                { label: 'Vyhráno', value: deals.filter(d => d.stage === 'won').reduce((s,d) => s+(d.value||0), 0) },
              ].map(row => (
                <div key={row.label}>
                  <p className="font-oswald text-xs uppercase text-gray-500">{row.label}</p>
                  <p className="font-oswald text-xl font-bold">{formatCZK(row.value)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Contact detail dialog ── */}
      <ContactDetailDialog
        contact={detailContact}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onUpdated={() => { fetchContacts(); if (detailContact) setDetailContact(c => contacts.find(x => x.id === c?.id) || c); }}
        clients={clients}
        users={users}
      />

      {/* ── Contact create/edit dialog ── */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-2 border-black rounded-none shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
          <DialogHeader>
            <DialogTitle className="font-oswald text-xl uppercase">
              {editingContact ? 'Upravit kontakt' : 'Nový kontakt'}
            </DialogTitle>
          </DialogHeader>
          <ContactForm data={contactForm} onChange={(k, v) => setContactForm(p => ({ ...p, [k]: v }))} clients={clients} users={users} />
          <div className="flex gap-3 pt-2">
            <Button onClick={handleContactSave} disabled={contactSaving}
              className="bg-[#F4E600] text-black font-oswald uppercase border-2 border-black rounded-none shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] transition-all">
              {contactSaving ? 'Ukládání...' : editingContact ? 'Uložit změny' : 'Vytvořit kontakt'}
            </Button>
            <Button variant="outline" onClick={() => setContactDialogOpen(false)}
              className="border-2 border-black rounded-none">Zrušit</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Deal create/edit dialog ── */}
      <Dialog open={dealDialogOpen} onOpenChange={setDealDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-2 border-black rounded-none shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
          <DialogHeader>
            <DialogTitle className="font-oswald text-xl uppercase">
              {editingDeal ? 'Upravit příležitost' : 'Nová příležitost'}
            </DialogTitle>
          </DialogHeader>
          <DealForm data={dealForm} onChange={(k, v) => setDealForm(p => ({ ...p, [k]: v }))} contacts={contacts} clients={clients} users={users} />
          <div className="flex gap-3 pt-2">
            <Button onClick={handleDealSave} disabled={dealSaving}
              className="bg-[#F4E600] text-black font-oswald uppercase border-2 border-black rounded-none shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] transition-all">
              {dealSaving ? 'Ukládání...' : editingDeal ? 'Uložit změny' : 'Vytvořit příležitost'}
            </Button>
            <Button variant="outline" onClick={() => setDealDialogOpen(false)}
              className="border-2 border-black rounded-none">Zrušit</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRM;